import { supabase } from '../utils/supabase';

/**
 * Search Service
 * Handles all search and discovery operations
 */
class SearchService {
  /**
   * Search vendors with advanced text search
   * @param {string} searchTerm - Search query
   * @param {Object} filters - Additional filters
   * @returns {Promise<Object>} Result with matching vendors
   */
  async searchVendors(searchTerm, filters = {}) {
    try {
      let query = supabase
        .from('vendor_profiles')
        .select(`
          *,
          vendor_gallery!vendor_id (
            id,
            image_url,
            is_featured
          )
        `, { count: 'exact' });

      // Full-text search across multiple fields
      if (searchTerm && searchTerm.trim()) {
        const searchPattern = `%${searchTerm}%`;
        query = query.or(
          `business_name.ilike.${searchPattern},` +
          `description.ilike.${searchPattern},` +
          `category.ilike.${searchPattern},` +
          `city.ilike.${searchPattern},` +
          `state.ilike.${searchPattern}`
        );
      }

      // Apply additional filters
      if (filters.category) {
        query = query.eq('category', filters.category);
      }

      if (filters.city) {
        query = query.eq('city', filters.city);
      }

      if (filters.state) {
        query = query.eq('state', filters.state);
      }

      if (filters.minPrice) {
        query = query.gte('starting_price', filters.minPrice);
      }

      if (filters.maxPrice) {
        query = query.lte('starting_price', filters.maxPrice);
      }

      if (filters.isVerified !== undefined) {
        query = query.eq('is_verified', filters.isVerified);
      }

      // Apply sorting
      const sortBy = filters.sortBy || 'created_at';
      query = query.order(sortBy, { ascending: false });

      // Apply pagination
      const limit = filters.limit || 20;
      query = query.limit(limit);

      const { data, error, count } = await query;

      if (error) {
        console.error('Error searching vendors:', error);
        return { success: false, error: error.message };
      }

      return {
        success: true,
        data: data || [],
        count,
        query: searchTerm
      };
    } catch (error) {
      console.error('Error in searchVendors:', error);
      return { success: false, error: 'Failed to search vendors' };
    }
  }

  /**
   * Get search suggestions based on partial query
   * @param {string} query - Partial search query
   * @returns {Promise<Object>} Result with suggestions
   */
  async getSuggestions(query) {
    try {
      if (!query || query.trim().length < 2) {
        return { success: true, data: [] };
      }

      const searchPattern = `%${query}%`;

      // Search in business names and categories
      const { data: vendors, error } = await supabase
        .from('vendor_profiles')
        .select('business_name, category, city, state')
        .or(`business_name.ilike.${searchPattern},category.ilike.${searchPattern}`)
        .limit(10);

      if (error) {
        console.error('Error fetching suggestions:', error);
        return { success: false, error: error.message };
      }

      // Format suggestions
      const suggestions = [];
      const seen = new Set();

      vendors?.forEach(vendor => {
        // Add business name suggestion
        if (vendor.business_name && !seen.has(vendor.business_name.toLowerCase())) {
          suggestions.push({
            type: 'vendor',
            text: vendor.business_name,
            subtext: `${vendor.category} in ${vendor.city}, ${vendor.state}`
          });
          seen.add(vendor.business_name.toLowerCase());
        }

        // Add category suggestion
        if (vendor.category && !seen.has(vendor.category.toLowerCase())) {
          suggestions.push({
            type: 'category',
            text: vendor.category,
            subtext: 'Category'
          });
          seen.add(vendor.category.toLowerCase());
        }
      });

      return {
        success: true,
        data: suggestions.slice(0, 8) // Limit to 8 suggestions
      };
    } catch (error) {
      console.error('Error in getSuggestions:', error);
      return { success: false, error: 'Failed to fetch suggestions' };
    }
  }

  /**
   * Get trending/popular searches
   * @returns {Promise<Object>} Result with trending searches
   */
  async getTrendingSearches() {
    try {
      // Get most popular categories and recent searches
      // For now, return popular categories
      const { data, error } = await supabase
        .from('vendor_profiles')
        .select('category')
        .not('category', 'is', null);

      if (error) {
        console.error('Error fetching trending searches:', error);
        return { success: false, error: error.message };
      }

      // Count occurrences of each category
      const categoryCount = {};
      data.forEach(item => {
        if (item.category) {
          categoryCount[item.category] = (categoryCount[item.category] || 0) + 1;
        }
      });

      // Sort by count and get top 10
      const trending = Object.entries(categoryCount)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 10)
        .map(([category, count]) => ({
          text: category,
          type: 'category',
          count
        }));

      return { success: true, data: trending };
    } catch (error) {
      console.error('Error in getTrendingSearches:', error);
      return { success: false, error: 'Failed to fetch trending searches' };
    }
  }

  /**
   * Search by location with radius
   * @param {Object} location - Location coordinates {lat, lng}
   * @param {number} radiusMiles - Search radius in miles
   * @param {Object} filters - Additional filters
   * @returns {Promise<Object>} Result with nearby vendors
   */
  async searchByLocation(location, radiusMiles = 50, filters = {}) {
    try {
      // Note: This requires PostGIS extension for proper geospatial queries
      // For now, we'll do a simple state/city filter
      // TODO: Implement proper geospatial search with PostGIS

      let query = supabase
        .from('vendor_profiles')
        .select(`
          *,
          vendor_gallery!vendor_id (
            id,
            image_url,
            is_featured
          )
        `, { count: 'exact' });

      // Apply filters
      if (filters.state) {
        query = query.eq('state', filters.state);
      }

      if (filters.city) {
        query = query.eq('city', filters.city);
      }

      if (filters.category) {
        query = query.eq('category', filters.category);
      }

      const { data, error, count } = await query.limit(filters.limit || 20);

      if (error) {
        console.error('Error searching by location:', error);
        return { success: false, error: error.message };
      }

      return {
        success: true,
        data: data || [],
        count
      };
    } catch (error) {
      console.error('Error in searchByLocation:', error);
      return { success: false, error: 'Failed to search by location' };
    }
  }

  /**
   * Get all unique cities where vendors are available
   * @param {string} state - Optional state filter
   * @returns {Promise<Object>} Result with cities
   */
  async getAvailableCities(state = null) {
    try {
      let query = supabase
        .from('vendor_profiles')
        .select('city, state')
        .not('city', 'is', null);

      if (state) {
        query = query.eq('state', state);
      }

      const { data, error } = await query;

      if (error) {
        console.error('Error fetching cities:', error);
        return { success: false, error: error.message };
      }

      // Get unique cities with counts
      const cityMap = {};
      data.forEach(item => {
        if (item.city && item.state) {
          const key = `${item.city}, ${item.state}`;
          cityMap[key] = (cityMap[key] || 0) + 1;
        }
      });

      const cities = Object.entries(cityMap)
        .map(([location, count]) => {
          const [city, state] = location.split(', ');
          return { city, state, vendorCount: count };
        })
        .sort((a, b) => b.vendorCount - a.vendorCount);

      return { success: true, data: cities };
    } catch (error) {
      console.error('Error in getAvailableCities:', error);
      return { success: false, error: 'Failed to fetch available cities' };
    }
  }

  /**
   * Get all unique states where vendors are available
   * @returns {Promise<Object>} Result with states
   */
  async getAvailableStates() {
    try {
      const { data, error } = await supabase
        .from('vendor_profiles')
        .select('state')
        .not('state', 'is', null);

      if (error) {
        console.error('Error fetching states:', error);
        return { success: false, error: error.message };
      }

      // Get unique states with counts
      const stateMap = {};
      data.forEach(item => {
        if (item.state) {
          stateMap[item.state] = (stateMap[item.state] || 0) + 1;
        }
      });

      const states = Object.entries(stateMap)
        .map(([state, count]) => ({ state, vendorCount: count }))
        .sort((a, b) => b.vendorCount - a.vendorCount);

      return { success: true, data: states };
    } catch (error) {
      console.error('Error in getAvailableStates:', error);
      return { success: false, error: 'Failed to fetch available states' };
    }
  }

  /**
   * Advanced search with multiple criteria
   * @param {Object} criteria - Search criteria
   * @returns {Promise<Object>} Result with matching vendors
   */
  async advancedSearch(criteria) {
    try {
      let query = supabase
        .from('vendor_profiles')
        .select(`
          *,
          vendor_gallery!vendor_id (
            id,
            image_url,
            is_featured
          ),
          vendor_packages!vendor_id (
            id,
            name,
            price
          )
        `, { count: 'exact' });

      // Apply all criteria
      if (criteria.category) {
        query = query.eq('category', criteria.category);
      }

      if (criteria.city) {
        query = query.eq('city', criteria.city);
      }

      if (criteria.state) {
        query = query.eq('state', criteria.state);
      }

      if (criteria.priceMin) {
        query = query.gte('starting_price', criteria.priceMin);
      }

      if (criteria.priceMax) {
        query = query.lte('starting_price', criteria.priceMax);
      }

      if (criteria.isVerified !== undefined) {
        query = query.eq('is_verified', criteria.isVerified);
      }

      if (criteria.isFeatured !== undefined) {
        query = query.eq('is_featured', criteria.isFeatured);
      }

      if (criteria.culturalSpecialties && criteria.culturalSpecialties.length > 0) {
        query = query.contains('cultural_specialties', criteria.culturalSpecialties);
      }

      if (criteria.languages && criteria.languages.length > 0) {
        query = query.contains('languages', criteria.languages);
      }

      // Text search
      if (criteria.searchText) {
        const searchPattern = `%${criteria.searchText}%`;
        query = query.or(
          `business_name.ilike.${searchPattern},` +
          `description.ilike.${searchPattern}`
        );
      }

      // Sorting
      if (criteria.sortBy) {
        const sortOrder = criteria.sortOrder === 'asc';
        query = query.order(criteria.sortBy, { ascending: sortOrder });
      } else {
        query = query.order('total_bookings', { ascending: false });
      }

      // Pagination
      const page = criteria.page || 1;
      const limit = criteria.limit || 20;
      const from = (page - 1) * limit;
      const to = from + limit - 1;
      query = query.range(from, to);

      const { data, error, count } = await query;

      if (error) {
        console.error('Error in advanced search:', error);
        return { success: false, error: error.message };
      }

      return {
        success: true,
        data: data || [],
        count,
        page,
        totalPages: Math.ceil(count / limit)
      };
    } catch (error) {
      console.error('Error in advancedSearch:', error);
      return { success: false, error: 'Failed to perform advanced search' };
    }
  }
}

// Export singleton instance
const searchService = new SearchService();
export default searchService;
